import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import BulkWalkthroughsDialogueSkeleton from 'src/components/BulkOperations/_Shared/skeletons/BulkWalkthroughsDialogueSkeleton';
import Button from 'src/components/Button';
import {
  usePropertySuitesBulkDialogues,
  UsePropertySuitesReturn,
} from 'src/SharedCustomHooks/UsePropertySuites/usePropertySuitesBulkDialogues';
import BulkActivateWalkthroughs from 'src/components/BulkOperations/Walkthroughs/BulkActivateWalkthroughs/BulkActivateWalkthroughs';
import BulkWalkthroughsSummary from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughSummary/BulKWalkthroughSummary';
import {
  getWalkthroughSummaryStats,
  IWalkthroughSummaryStats,
} from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughSummary/walkthroughSummaryStats';
import WalkthroughSummaryHeaderContent from 'src/components/BulkOperations/Walkthroughs/WalkthroughSummaryHeaderContent/WalkthroughSummaryHeaderContent';
import {
  useBulkActivateWalkthroughs,
  UseBulkActivateWalkthroughsReturn,
} from 'src/components/BulkOperations/Walkthroughs/BulkActivateWalkthroughs/Custom-Hooks/useBulkActivateWalkthroughs';
import {
  IUseCalcomConnection,
  useCalcomOauthConnection,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useCalcomOauthConnection';
import {
  IUseBookingLinkSelection,
  useBookingLinkSelection,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useBookingLinkSelection/useBookingLinkSelection';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import { IWalkthroughModel } from 'src/services/client-types';
import { variables } from 'src/Tenants';

export interface WalkthroughActions {
  toggle: (id: string, checked: boolean) => void;
  selectAllForUnit: (walkthroughs: IWalkthroughModel[]) => void;
  deselectAllForUnit: (walkthroughs: IWalkthroughModel[]) => void;
  selectAll: () => void;
  deselectAll: () => void;
}

export interface ActivationActions {
  setActivate: (value: boolean) => void;
  setSendEmail: (value: boolean) => void;
}

export interface UnitActions {
  toggle: (
    unitId: string,
    unit: UnitBulkOperationDto,
    isChecked: boolean
  ) => Promise<void>;
  getWalkthroughs: (unitId: string) => IWalkthroughModel[];
}

interface IBulkActivateWalkthroughsDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  propertyId: string;
  isLoading?: boolean;
}

const BulkActivateWalkthroughsDialogue = ({
  isOpen,
  onClose,
  userId,
  propertyId,
  isLoading = false,
}: IBulkActivateWalkthroughsDialogueProps) => {
  const {
    simpleSuitesForWalkthroughDialogue,
    isLoading: suitesLoading,
    error: suitesError,
    refetch: refetchSuites,
    suites: detailedSuites,
  }: UsePropertySuitesReturn = usePropertySuitesBulkDialogues({
    propertyId,
    userId,
    enabled: isOpen,
  });

  const {
    selectedBookingLinkId,
    selectBookingLink,
    resetBookingLink,
  }: IUseBookingLinkSelection = useBookingLinkSelection();

  const {
    selectedUnitIds,
    handleUnitToggle,
    isLoadingUnit,
    getWalkthroughsForUnit,
    activateWalkthroughs,
    setActivateWalkthroughs,
    sendSchedulingEmail,
    setSendSchedulingEmail,
    selectedWalkthroughIds,
    handleWalkthroughToggle,
    handleSelectAllForUnit,
    walkthroughsByUnit,
    handleDeselectAllForUnit,
    onSelectAllWalkthroughs,
    onDeselectAllWalkthroughs,
    isFetchingWalkthroughs,
    performBulkActivation,
    bulkActivateResponseData,
    isActivationSuccess,
    resetActivationState,
    isSubmitting,
    confirmActivation,
    setShowConfirmation,
    showConfirmation,
    error,
    clearError,
  }: UseBulkActivateWalkthroughsReturn = useBulkActivateWalkthroughs(
    simpleSuitesForWalkthroughDialogue,
    selectedBookingLinkId
  );

  const {
    isConnected: isCalcomConnected,
    connectedCalendars,
  }: IUseCalcomConnection = useCalcomOauthConnection();

  const unitActions: UnitActions = {
    toggle: handleUnitToggle,
    getWalkthroughs: getWalkthroughsForUnit,
  };

  const walkthroughActions: WalkthroughActions = {
    toggle: handleWalkthroughToggle,
    selectAllForUnit: handleSelectAllForUnit,
    deselectAllForUnit: handleDeselectAllForUnit,
    selectAll: onSelectAllWalkthroughs,
    deselectAll: onDeselectAllWalkthroughs,
  };

  const activationActions: ActivationActions = {
    setActivate: setActivateWalkthroughs,
    setSendEmail: setSendSchedulingEmail,
  };

  const handleClose = () => {
    resetActivationState();
    resetBookingLink();
    onClose();
  };

  const handleRetry = () => {
    refetchSuites();
  };

  const suiteName: string = variables.suiteName;
  const dialogueMinHeight: string = '50vh';

  const isSummaryVisible: boolean =
    isActivationSuccess && !!bulkActivateResponseData;

  const summaryStats: IWalkthroughSummaryStats | null = isSummaryVisible
    ? getWalkthroughSummaryStats(bulkActivateResponseData)
    : null;

  if (isLoading || suitesLoading) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Activate Walkthroughs"
        description={`Activate existing walkthroughs for multiple ${suiteName}`}
        minHeight={dialogueMinHeight}
      >
        <BulkWalkthroughsDialogueSkeleton />
      </GenericDialogue>
    );
  }

  if (suitesError) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Activate Walkthroughs"
        description={`Activate existing walkthroughs for multiple ${suiteName}`}
        minHeight={dialogueMinHeight}
      >
        <div className="bulk-activate-walkthroughs__error">
          <p>Error: {suitesError}</p>
          <Button
            color="teal"
            size="small"
            label="Retry"
            onClick={handleRetry}
          />
        </div>
      </GenericDialogue>
    );
  }

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isActivationSuccess
          ? 'Walkthroughs Activated'
          : 'Bulk Activate Walkthroughs'
      }
      description={
        isActivationSuccess
          ? 'Summary of activated walkthroughs'
          : `Activate existing walkthroughs for multiple ${suiteName}`
      }
      minHeight={dialogueMinHeight}
      subheader={
        summaryStats ? (
          <WalkthroughSummaryHeaderContent {...summaryStats} mode="activation" />
        ) : undefined
      }
      footer={
        isSummaryVisible ? (
          <Button color="teal" size="small" label="Done" onClick={handleClose} />
        ) : undefined
      }
    >
      {isSummaryVisible ? (
        <BulkWalkthroughsSummary
          walkthroughs={bulkActivateResponseData}
          isCalcomConnected={isCalcomConnected}
        />
      ) : (
        <BulkActivateWalkthroughs
          suites={simpleSuitesForWalkthroughDialogue}
          detailedSuites={detailedSuites}
          onClose={handleClose}
          selectedUnitIds={selectedUnitIds}
          isLoadingUnit={isLoadingUnit}
          isFetchingWalkthroughs={isFetchingWalkthroughs}
          activateWalkthroughs={activateWalkthroughs}
          sendSchedulingEmail={sendSchedulingEmail}
          selectedWalkthroughIds={selectedWalkthroughIds}
          walkthroughsByUnit={walkthroughsByUnit}
          performBulkActivation={performBulkActivation}
          unitActions={unitActions}
          walkthroughActions={walkthroughActions}
          activationActions={activationActions}
          isSubmitting={isSubmitting}
          confirmActivation={confirmActivation}
          setShowConfirmation={setShowConfirmation}
          showConfirmation={showConfirmation}
          error={error}
          clearError={clearError}
          propertyId={propertyId}
          isCalcomConnected={isCalcomConnected}
          connectedCalendars={connectedCalendars}
          selectedBookingLinkId={selectedBookingLinkId}
          onSelectBookingLink={selectBookingLink}
        />
      )}
    </GenericDialogue>
  );
};

export default BulkActivateWalkthroughsDialogue;
